import React, { useMemo } from "react";
import { ClockIcon, DocumentIcon } from "@heroicons/react/outline";
import { formatDateIntoDMYToDisplay } from "../../../../Utils/utils";

// Helper function to get a valid date for sorting
const getValidDate = (item) => {
  const dateStr = item.updated_at || item.created_at;
  if (!dateStr) return new Date(0); // Fallback to epoch for items without dates
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? new Date(0) : date;
};

const RecentFiles = ({
  files = [],
  folders = [],
  onFileClick,
  onFolderClick,
  maxItems = 5,
}) => {
  // Combine and sort by updated_at (fallback to created_at)
  const recentItems = useMemo(() => {
    const allItems = [
      ...files.map((f) => ({
        ...f,
        type: "file",
        displayName: f.description || f.name || f.label || "Untitled File",
      })),
      ...folders.map((f) => ({ ...f, type: "folder", displayName: f.name })),
    ];

    return allItems
      .sort((a, b) => getValidDate(b) - getValidDate(a))
      .slice(0, maxItems);
  }, [files, folders, maxItems]);

  if (recentItems.length === 0) {
    return null;
  }

  return (
    <div className='bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6'>
      <div className='flex items-center gap-2 mb-4'>
        <ClockIcon className='h-5 w-5 text-gray-500' />
        <h3 className='text-sm font-semibold text-gray-700'>Recent Files</h3>
      </div>
      <div className='space-y-3'>
        {recentItems.map((item) => {
          const isFile = item.type === "file";
          const isClickable = isFile
            ? typeof onFileClick === "function"
            : typeof onFolderClick === "function";

          return (
            <div
              key={item.uuid || `${item.type}-${item.id}`}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={() => {
                if (isFile && onFileClick) {
                  onFileClick(item);
                } else if (!isFile && onFolderClick) {
                  onFolderClick(item);
                }
              }}
              onKeyDown={(e) => {
                if (!isClickable) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (isFile && onFileClick) onFileClick(item);
                  else if (!isFile && onFolderClick) onFolderClick(item);
                }
              }}
              className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                isClickable
                  ? "hover:bg-blue-50 cursor-pointer"
                  : "hover:bg-gray-50 cursor-default"
              }`}
              title={item.displayName}
            >
              <div className='flex-shrink-0'>
                {isFile ? (
                  <DocumentIcon className='h-5 w-5 text-gray-400' />
                ) : (
                  <svg
                    className='h-5 w-5 text-amber-500'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
                    />
                  </svg>
                )}
              </div>
              <div className='flex-1 min-w-0'>
                <p
                  className='text-sm font-medium text-gray-900 truncate whitespace-nowrap mb-0.5'
                  title={item.displayName}
                >
                  {item.displayName}
                </p>
                <p className='text-xs text-gray-500 truncate whitespace-nowrap'>
                  {formatDateIntoDMYToDisplay(
                    item.updated_at || item.created_at
                  )}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentFiles;
